import { type CalendarDto, type CalendarItemDto } from '../../services/api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { addDays, buildPropertyLink, buildTenantLink, dateFromIso, formatDate, formatRangeLabel, getUrgencyClass, getUrgencyLabel, itemIcon, itemMeta, itemTitle, toIsoDate, translateSource, type CalendarSourceFilter, type CalendarViewMode, esc } from './calendar-shared';

export class RcCalendarView extends HTMLElement {
  data: CalendarDto | null = null;
  viewMode: CalendarViewMode = 'week';
  sourceFilter: CalendarSourceFilter = 'all';
  loading = false;
  errorMessage = '';

  render(): void {
    if (!this.data) return;
    const items = this.getFilteredItems();
    this.innerHTML = `
      <div class="calendar-shell${this.loading ? ' is-loading' : ''}" aria-busy="${this.loading ? 'true' : 'false'}">
      <div class="page-header page-header--calendar">
        <div>
          <h1>${t('calendar.title')}</h1>
          <p class="page-subtitle">${t('calendar.subtitle')}</p>
        </div>
      </div>
      ${this.renderSummary()}
      <div class="card calendar-toolbar">
        <div class="calendar-toolbar-group">
          <button class="btn btn-outline btn-sm" data-action="prev" ${this.loading ? 'disabled' : ''}>${icon('arrow-left', { size: 14 })} ${t('common.previous')}</button>
          <button class="btn btn-outline btn-sm" data-action="today" ${this.loading ? 'disabled' : ''}>${t('calendar.todayButton')}</button>
          <button class="btn btn-outline btn-sm" data-action="next" ${this.loading ? 'disabled' : ''}>${t('common.next')} ${icon('arrow-right', { size: 14 })}</button>
        </div>
        <div class="calendar-range-label">${formatRangeLabel(this.data)}</div>
        <div class="calendar-toolbar-group">
          <button class="btn btn-outline btn-sm ${this.viewMode === 'day' ? 'active' : ''}" data-view="day" ${this.loading ? 'disabled' : ''}>${t('calendar.viewDay')}</button>
          <button class="btn btn-outline btn-sm ${this.viewMode === 'week' ? 'active' : ''}" data-view="week" ${this.loading ? 'disabled' : ''}>${t('calendar.viewWeek')}</button>
        </div>
      </div>
      ${this.renderFilters(items)}
      ${this.renderHighlights(items)}
      ${this.errorMessage ? `<div class="calendar-inline-error card">${this.errorMessage}</div>` : ''}
      <div class="calendar-content">
      ${this.viewMode === 'day' ? this.renderDayView(items) : this.renderWeekView(items)}
      </div>
      ${this.loading ? `<div class="calendar-loading-overlay" aria-hidden="true"><div class="calendar-loading-pill">${t('common.loading')}</div></div>` : ''}
      </div>
    `;

    this.querySelector('[data-action="prev"]')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('calendar-nav', { bubbles: true, detail: { direction: 'prev' } })));
    this.querySelector('[data-action="next"]')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('calendar-nav', { bubbles: true, detail: { direction: 'next' } })));
    this.querySelector('[data-action="today"]')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('calendar-nav', { bubbles: true, detail: { direction: 'today' } })));
    this.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('calendar-view-change', { bubbles: true, detail: { viewMode: button.dataset.view } }))));
    this.querySelectorAll<HTMLButtonElement>('[data-source-filter]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('calendar-filter-change', { bubbles: true, detail: { sourceFilter: button.dataset.sourceFilter } }))));
  }

  private renderSummary(): string {
    if (!this.data) return '';
    const cards = [
      ['total', this.data.summary.totalCount, icon('calendar', { size: 16 }), t('calendar.summary.total')],
      ['reminders', this.data.summary.reminderCount, icon('bell', { size: 16 }), t('calendar.summary.reminders')],
      ['documents', this.data.summary.documentExpiryCount, icon('file-text', { size: 16 }), t('calendar.summary.documents')],
      ['agreements', this.data.summary.agreementEndCount, icon('key', { size: 16 }), t('calendar.summary.agreements')],
      ['events', this.data.summary.eventCount, icon('layout', { size: 16 }), t('calendar.summary.events')],
    ];
    return `<div class="calendar-summary-grid">${cards.map(([className, value, iconHtml, label]) => `
      <div class="calendar-summary-card calendar-summary-card--${className}">
        <div class="calendar-summary-icon">${iconHtml}</div>
        <div class="calendar-summary-value">${value}</div>
        <div class="calendar-summary-label">${label}</div>
      </div>`).join('')}</div>`;
  }

  private getFilteredItems(): CalendarItemDto[] {
    if (!this.data) return [];
    if (this.sourceFilter === 'all') return this.data.items;
    return this.data.items.filter((item) => item.sourceType === this.sourceFilter);
  }

  private renderFilters(items: CalendarItemDto[]): string {
    const filters: Array<{ key: CalendarSourceFilter; label: string; count: number }> = [
      { key: 'all', label: t('calendar.filter.all'), count: this.data?.items.length ?? 0 },
      { key: 'Reminder', label: t('calendar.source.reminder'), count: this.data?.items.filter((item) => item.sourceType === 'Reminder').length ?? 0 },
      { key: 'DocumentExpiry', label: t('calendar.source.documentExpiry'), count: this.data?.items.filter((item) => item.sourceType === 'DocumentExpiry').length ?? 0 },
      { key: 'AgreementEnd', label: t('calendar.source.agreementEnd'), count: this.data?.items.filter((item) => item.sourceType === 'AgreementEnd').length ?? 0 },
      { key: 'TimelineEvent', label: t('calendar.source.timelineEvent'), count: this.data?.items.filter((item) => item.sourceType === 'TimelineEvent').length ?? 0 },
    ];

    return `<div class="calendar-filter-row">${filters.map((filter) => `
      <button class="calendar-filter-chip${this.sourceFilter === filter.key ? ' is-active' : ''}" data-source-filter="${filter.key}" ${this.loading ? 'disabled' : ''}>
        <span>${filter.label}</span>
        <span class="calendar-filter-count">${filter.count}</span>
      </button>
    `).join('')}</div>`;
  }

  private renderHighlights(items: CalendarItemDto[]): string {
    const urgentItems = items
      .filter((item) => getUrgencyClass(item) !== 'upcoming')
      .sort((left, right) => left.date.localeCompare(right.date))
      .slice(0, 3);

    if (urgentItems.length === 0) {
      return '';
    }

    return `<div class="calendar-highlights card">
      <div class="calendar-highlights-header">
        <div>
          <h2>${t('calendar.highlights.title')}</h2>
          <p>${t('calendar.highlights.subtitle')}</p>
        </div>
      </div>
      <div class="calendar-highlight-list">${urgentItems.map((item) => `
        <a class="calendar-highlight-item urgency-${getUrgencyClass(item)}" href="${item.targetPath}">
          <span class="calendar-highlight-badge">${getUrgencyLabel(item)}</span>
          <strong>${esc(itemTitle(item))}</strong>
          <span>${esc(translateSource(item))} · ${esc(formatDate(item.date, { weekday: 'short', day: 'numeric', month: 'short' }))}</span>
        </a>
      `).join('')}</div>
    </div>`;
  }

  private renderDayItems(items: CalendarItemDto[]): string {
    if (items.length === 0) {
      return `<div class="calendar-day-empty">${t('calendar.empty')}</div>`;
    }
    return `<div class="calendar-item-list">${items.map((item) => `
      <article class="calendar-item urgency-${getUrgencyClass(item)}">
        <a class="calendar-item-main" href="${item.targetPath}">
          <div class="calendar-item-badge calendar-item-badge--${item.sourceType.toLowerCase()}">${itemIcon(item)} ${translateSource(item)}</div>
          ${getUrgencyLabel(item) ? `<div class="calendar-item-urgency">${esc(getUrgencyLabel(item) || '')}</div>` : ''}
          <div class="calendar-item-title">${esc(itemTitle(item))}</div>
          ${itemMeta(item) ? `<div class="calendar-item-meta">${itemMeta(item)}</div>` : ''}
          ${item.description ? `<div class="calendar-item-description">${esc(item.description)}</div>` : ''}
        </a>
        <div class="calendar-item-actions">
          ${buildPropertyLink(item) ? `<a class="calendar-item-action" href="${buildPropertyLink(item)}">${t('calendar.quickAction.property')}</a>` : ''}
          ${buildTenantLink(item) ? `<a class="calendar-item-action" href="${buildTenantLink(item)}">${t('calendar.quickAction.tenant')}</a>` : ''}
        </div>
      </article>`).join('')}</div>`;
  }

  private renderDayView(items: CalendarItemDto[]): string {
    if (!this.data) return '';
    return `<section class="calendar-panel calendar-panel--single"><div class="calendar-panel-header"><h2>${formatDate(this.data.startDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2></div>${this.renderDayItems(items)}</section>`;
  }

  private renderWeekView(items: CalendarItemDto[]): string {
    if (!this.data) return '';
    const columns: string[] = [];
    let current = dateFromIso(this.data.startDate);
    const end = dateFromIso(this.data.endDate);
    while (current <= end) {
      const dateKey = toIsoDate(current);
      const dayItems = items.filter((item) => item.date === dateKey);
      columns.push(`<section class="calendar-day-column"><div class="calendar-day-header"><div class="calendar-day-label">${formatDate(dateKey, { weekday: 'short' })}</div><div class="calendar-day-date">${formatDate(dateKey, { day: 'numeric', month: 'short' })}</div></div>${this.renderDayItems(dayItems)}</section>`);
      current = addDays(current, 1);
    }
    return `<div class="calendar-week-grid">${columns.join('')}</div>`;
  }
}

if (!customElements.get('rc-calendar-view')) {
  customElements.define('rc-calendar-view', RcCalendarView);
}
